import {
  Alert,
  Box,
  Button,
  Grid,
  Icon,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { buildQuery, useDashboard } from "../../api/ESocialJTServiceApi";
import Page from "../../components/layout/page/Page";
import ValoresDashboard from "../../components/operacional/ValoresDashboard";
import FiltrosPessoas, {
  filtrosPadrao,
  parametrosFiltro
} from "../../components/operacional/FiltrosPessoas";
import {
  EstadoConsulta,
  Indicadores
} from "../../components/operacional/Elementos";
import {
  competenciaExibicao,
  numero,
  TIPOS
} from "../../shared/apresentacaoEvento";

export default function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const filtro = useMemo(
    () => ({
      ...filtrosPadrao(),
      ...Object.fromEntries(new URLSearchParams(key)),
      incluirArquivados:
        new URLSearchParams(key).get("incluirArquivados") === "true"
    }),
    [key]
  );
  const efetivo = parametrosFiltro(filtro);
  const query = useDashboard(efetivo);
  const d = query.data;
  const r = d?.resumo || {};
  function aplicar(f) {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v !== false) p.set(k, String(v));
    });
    setParams(p);
  }
  const linkOcorrencias = buildQuery("/", efetivo);
  return (
    <Page maxWidth="lg">
      <Box
        sx={{
          my: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          color: "white",
          background: "linear-gradient(120deg,#12364b,#006b78)"
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 2, opacity: 0.8 }}
            >
              VISÃO OPERACIONAL · ESOCIAL
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.85 }}>
              Competências, pessoas e situações em um único painel.
            </Typography>
          </Box>
          <Button
            component={Link}
            to={linkOcorrencias}
            variant="outlined"
            sx={{
              color: "white",
              borderColor: "rgba(255,255,255,.6)",
              alignSelf: "center",
              whiteSpace: "nowrap"
            }}
            startIcon={<Icon>list_alt</Icon>}
          >
            Ver ocorrências
          </Button>
        </Stack>
      </Box>
      <FiltrosPessoas
        value={filtro}
        periodo
        onSubmit={aplicar}
        busy={query.isFetching}
      />
      <EstadoConsulta query={query}>
        {d && (
          <>
            <Indicadores
              items={[
                {
                  label: "Ocorrências",
                  value: numero(r.total),
                  icon: "layers"
                },
                { label: "Pessoas", value: numero(r.pessoas), icon: "people" },
                {
                  label: "Com sucesso",
                  value: numero(r.sucesso),
                  icon: "check_circle",
                  color: "success.main"
                },
                {
                  label: "Precisam de atenção",
                  value: numero(r.erros),
                  icon: "error_outline",
                  color: "error.main"
                },
                {
                  label: "Aguardando",
                  value: numero(r.aguardando),
                  icon: "schedule",
                  color: "warning.main"
                }
              ]}
            />
            <ValoresDashboard dashboard={d} filtros={efetivo} />
            {Number(r.total) === 0 ? (
              <Alert severity="info">
                Não há ocorrências no recorte selecionado.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Grafico
                    titulo="Distribuição por evento"
                    subtitulo="Clique no tipo para abrir as ocorrências deste recorte."
                    rows={(d.porTipo || []).map((t) => ({
                      label: `S-${t.tipo || "—"} · ${
                        TIPOS[t.tipo] || "Sem tipo"
                      }`,
                      value: t.total,
                      erro: t.erros,
                      to: buildQuery("/", { ...efetivo, tipos: t.tipo })
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Grafico
                    titulo="Competências de apuração"
                    subtitulo="Eventos sem perApur são exibidos como sem competência."
                    rows={(d.porCompetencia || []).map((t) => ({
                      label: competenciaExibicao(t.competencia),
                      value: t.total
                    }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grafico
                    titulo="Recebimentos no período"
                    subtitulo="Volume diário de ocorrências recebidas pelo conector."
                    rows={(d.porDia || []).map((t) => ({
                      label: String(t.dia)
                        .slice(0, 10)
                        .split("-")
                        .reverse()
                        .join("/"),
                      value: t.total
                    }))}
                  />
                </Grid>
              </Grid>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Contagens de ocorrências: retificações e exclusões aparecem como
              registros próprios. Não representam a quantidade de folhas
              vigentes nem um total financeiro.
            </Typography>
          </>
        )}
      </EstadoConsulta>
    </Page>
  );
}
function Grafico({ titulo, subtitulo, rows }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.value)));
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: "100%" }}>
      <Typography variant="h6" fontWeight={600}>
        {titulo}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {subtitulo}
      </Typography>
      <Stack spacing={2} sx={{ maxHeight: 480, overflow: "auto" }}>
        {rows.map((r, i) => (
          <Box key={i}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography
                component={r.to ? Link : "span"}
                to={r.to}
                sx={{
                  fontSize: 14,
                  textDecoration: "none",
                  color: "text.primary"
                }}
              >
                {r.label}
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                {numero(r.value)}
              </Typography>
            </Stack>
            <Box
              role="img"
              aria-label={`${r.label}: ${r.value}${
                r.erro ? `, ${r.erro} com erro` : ""
              }`}
              sx={{
                height: 10,
                bgcolor: "grey.100",
                borderRadius: 5,
                overflow: "hidden",
                mt: 0.7
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${Math.max(1, (100 * r.value) / max)}%`,
                  bgcolor: "primary.main",
                  borderRadius: 5,
                  display: "flex",
                  justifyContent: "flex-end"
                }}
              >
                {Number(r.erro) > 0 && (
                  <Box
                    sx={{
                      height: "100%",
                      width: `${(100 * r.erro) / r.value}%`,
                      bgcolor: "error.main"
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
